/**
 * Auswahl fuer die Gesamtuebersicht (V-01) - rein und getestet.
 *
 * Die Uebersicht zeigt sechs Bloecke. Was in einem Block steht, soll nicht
 * noch einmal in einem anderen auftauchen: Ueberfaelliges und Heutiges haben
 * eigene Bloecke, die naechsten beiden Tage stehen im Kalender, der "Blick in
 * die Zukunft" zeigt, was danach kommt.
 */
import { istSchulstunde } from '@/domain/school/ferien';
import { addDays, datePart, formatDe } from '@/lib/datetime';
import type { DeadlineItem } from '@/domain/deadlines';
import type { CalendarOccurrence, LocalDate } from '@/domain/types';

/** Schulstunden eines Zeitraums - nur gezaehlt, sonst fluten sie jede Liste. */
export interface Schulstunden {
  anzahl: number;
  /** Beginn der ersten und Ende der letzten Stunde, "HH:mm". */
  von: string;
  bis: string;
}

export interface TerminListe {
  termine: CalendarOccurrence[];
  schule: Schulstunden | null;
}

const nachStart = (a: CalendarOccurrence, b: CalendarOccurrence) =>
  Number(b.allDay) - Number(a.allDay) || String(a.start).localeCompare(String(b.start)) || a.title.localeCompare(b.title, 'de');

/**
 * Termine, die in [von, bis) beginnen - ganztaegige zuerst, dann nach Beginn.
 * Mehrtaegige Termine, die vorher begonnen haben, stehen schon dort.
 */
export function termineZwischen(occ: CalendarOccurrence[], von: LocalDate, bis: LocalDate): TerminListe {
  const drin = occ.filter((o) => {
    const tag = datePart(String(o.start) as LocalDate);
    return tag >= von && tag < bis;
  });
  const stunden = drin.filter(istSchulstunde)
    .sort((a, b) => String(a.start).localeCompare(String(b.start)));
  const schule = stunden.length === 0 ? null : {
    anzahl: stunden.length,
    von: formatDe(String(stunden[0]!.start), 'HH:mm'),
    bis: formatDe(String(stunden.reduce((m, s) => (String(s.end) > m ? String(s.end) : m), String(stunden[0]!.end))), 'HH:mm'),
  };
  return { termine: drin.filter((o) => !istSchulstunde(o)).sort(nachStart), schule };
}

/** Termine eines einzelnen Tages. */
export const termineAm = (occ: CalendarOccurrence[], tag: LocalDate): TerminListe => termineZwischen(occ, tag, addDays(tag, 1));

/**
 * Deadlines fuer den "Blick in die Zukunft": alles bis `heute + tage`.
 *
 * Aufgaben erst ab morgen - ueberfaellige und heutige stehen schon in ihren
 * eigenen Bloecken. Projekte, Ziele und Klausuren bleiben auch dann stehen,
 * wenn ihre Frist heute ist oder schon verstrichen ist: dafuer gibt es sonst
 * keinen Block.
 */
export function zukunftsDeadlines(deadlines: DeadlineItem[], heute: LocalDate, tage: number): DeadlineItem[] {
  const grenze = addDays(heute, tage);
  return deadlines.filter((d) => d.date <= grenze && (d.kind !== 'aufgabe' || d.date > heute));
}
